import { Component, Input } from '@angular/core';
import { Router } from '@angular/router';
import { MaintenanceService } from 'app/maintenance/core/services/maintenance.service';
import { AuthenticationService } from '../core/services/authentication.service';
import { IAuthenticationMethod } from '../core/typings/IAuthentication';
import { BzgModalService } from 'bz-genome-angular';
import { I18nService } from 'i18n/i18n.service';
import { AuthenticationTypes, AuthenticationProperties } from '../core/enums/authentication-types.enum';
import { ModalConfirmationComponent } from 'app/shared/components/modal-confirmation/modal-confirmation.component';
import { AuthenticationListUtils } from './utils/authentication-list.utils';
import { ShowToastService } from 'app/shared/services/showToast.service';
import { CryptoDataService } from 'app/core/services/crypto.data.service';

@Component({
  selector: 'security-authentication-refactor',
  templateUrl: 'security-authentication-refactor.component.html',
  styleUrls: ['security-authentication-refactor.component.scss']
})
export class SecurityAuthenticationRefactorComponent  {
  metadata: any;
  selectItem: any;
  selectedMetadata: any;
  templateToShow: any;
  selectedMultipleAuthenticator: any;
  authenticatorList: any[] = [];
  templates: { [key: string]: any } = {};
  indexCurrentAuthMultiple: number = -1;
  hasPendingChanges: boolean = false;
  isMaintenanceWindowActive: boolean = false;
  addNewMultipleAuthenticator: boolean = false;
  currentFileName: string = '';
  initialAuthenticationType: string;
  inProgressMaintenance: string ='InProgess';
  selectedMethod: IAuthenticationMethod;
  isValidAuthentication: boolean = true;
  showErrorHeader: boolean = false;
  typeInvalid: string = '';
  subtypeInvalid: string = '';

  _authenticationData: any;
  _authenticationDataContent: any;
  _authenticationDataContentOriginal: any;
  
  @Input() enableNewTabAuthentication: any;

  // ==================== GETTERS ====================
  get subtypes(): any[] {
    if (this.selectedMethod?.type && this.metadata?.[this.selectedMethod.type]?.[AuthenticationProperties.CONFIG]?.[AuthenticationProperties.SUBTYPES]?.[AuthenticationProperties.OPTIONS]) {
      return this.metadata[this.selectedMethod.type][AuthenticationProperties.CONFIG][AuthenticationProperties.SUBTYPES][AuthenticationProperties.OPTIONS];
    }
    return [];
  }

  get flows(): any[] {
    if (this.selectedMethod?.type === AuthenticationTypes.OAUTH2 && 
        this.selectedMethod?.subtype && 
        this.metadata?.[this.selectedMethod.type]?.[AuthenticationProperties.CONFIG]?.[AuthenticationProperties.FLOWS]?.[AuthenticationProperties.OPTIONS]?.[this.selectedMethod.subtype]?.[AuthenticationProperties.OPTIONS]) {
      const flow = this.metadata[this.selectedMethod.type][AuthenticationProperties.CONFIG][AuthenticationProperties.FLOWS][AuthenticationProperties.OPTIONS][this.selectedMethod.subtype][AuthenticationProperties.OPTIONS];
          return flow;
    }
    return [];
  }

  get activeFlow(): any {
    if (this.selectedMethod?.type === AuthenticationTypes.OAUTH2 && 
        this.selectedMethod?.subtype && 
        this.metadata?.[this.selectedMethod.type]?.[AuthenticationProperties.CONFIG]?.[AuthenticationProperties.FLOWS]?.[AuthenticationProperties.OPTIONS]?.[this.selectedMethod.subtype]?.[AuthenticationProperties.OPTIONS]) {
      const methodIndex = this.findIndexMethodAuthentication();
      if (methodIndex !== -1 && this._authenticationDataContent.methods[methodIndex][AuthenticationProperties.OAUTH_OPTIONS]?.[AuthenticationProperties.ACTIVE_FLOW]) {
        return this._authenticationDataContent.methods[methodIndex][AuthenticationProperties.OAUTH_OPTIONS][AuthenticationProperties.ACTIVE_FLOW];
      }
    }
    return '';
  }

  set activeFlow(value: any) {
    if (this.selectedMethod?.type === AuthenticationTypes.OAUTH2 && 
        this.selectedMethod?.subtype && 
        this.metadata?.[this.selectedMethod.type]?.[AuthenticationProperties.CONFIG]?.[AuthenticationProperties.FLOWS]?.[AuthenticationProperties.OPTIONS]?.[this.selectedMethod.subtype]?.[AuthenticationProperties.OPTIONS]) {
      const methodIndex = this.findIndexMethodAuthentication();
      if (methodIndex !== -1) {
        // Ensure oauthOptions exists
        if (!this._authenticationDataContent.methods[methodIndex][AuthenticationProperties.OAUTH_OPTIONS]) {
          this._authenticationDataContent.methods[methodIndex][AuthenticationProperties.OAUTH_OPTIONS] = {};
        }
        // Assign the new value
        this._authenticationDataContent.methods[methodIndex][AuthenticationProperties.OAUTH_OPTIONS][AuthenticationProperties.ACTIVE_FLOW] = value;
        
        // Also update in selectedMethod to maintain synchronization
        if (!(this.selectedMethod as any)[AuthenticationProperties.OAUTH_OPTIONS]) {
          (this.selectedMethod as any)[AuthenticationProperties.OAUTH_OPTIONS] = {};
        }
        (this.selectedMethod as any)[AuthenticationProperties.OAUTH_OPTIONS][AuthenticationProperties.ACTIVE_FLOW] = value;
        this.getSelectedMetadata();
      }
    }
  }
  // ==================== END GETTERS ====================

  // ==================== CONSTRUCTOR ====================
  constructor(
    private _router: Router,
    private _maintenanceService: MaintenanceService,
    private _authenticationService: AuthenticationService,
    private _i18nService: I18nService,
    private _modalService: BzgModalService,
    private _authListUtils: AuthenticationListUtils,
    private _showToastService: ShowToastService,
    private _cryptoDataService: CryptoDataService
  ) {
  }

  // ==================== END CONSTRUCTOR ====================


  // ==================== ON INIT ====================
  ngOnInit() {
    this.getStateMaintenanceWindow();
    this.getAuthenticationMetadata();
    this.getTemplateAuthentication();
  }
  // ==================== END ON INIT ====================


  // ==================== SERVICES ====================
  getStateMaintenanceWindow() {
    this._maintenanceService.getStateMaintenanceWindow().subscribe(res => {
      this.isMaintenanceWindowActive = res && res?.State === this.inProgressMaintenance;
    });
  } 

  getAuthenticationMetadata() {
    this._authenticationService.getAuthenticationMetadataObservable().subscribe(res => {
      this.metadata = res;
      this.getAuthenticationData();
    });
  }

  getTemplateAuthentication() {
    this._authenticationService.getTemplateAuthentication().subscribe(response => {
      this.templates = response;
    });
  }

  getAuthenticationData() {
    this._authenticationService.getAuthenticationData().subscribe(response => {
      this._authenticationData = response;
      this._authenticationDataContent = this.formatContent(response);
      this._authenticationDataContentOriginal = JSON.parse(JSON.stringify(this._authenticationDataContent));
      this.checkAuthenticationIsValid();
      this.loadActiveAuthenticationType();
      this.loadAuthenticatorList();
      // Add 'Multiple' key to metadata if it doesn't exist
      if (!this.metadata[AuthenticationTypes.MULTIPLE]) {
        this.metadata[AuthenticationTypes.MULTIPLE] = {
          authenticators: {},
          type: AuthenticationTypes.MULTIPLE
        };
      }
    });
  }

  saveAuthenticationService(dataToSave, newSelectedItem?: any) {
    const dataArray = [dataToSave];
    this._authenticationService.putAuthentication(dataArray).subscribe({
      next: () => {
        if (newSelectedItem)
          this.selectItem = newSelectedItem;
        this._showToastService.showToast(this._i18nService.instant('GLOBAL.THE_OPERATION_WAS_SAVED_SUCCESSFULLY'), 'success');
        this.getAuthenticationData();
      },
      error: () => {
        if (newSelectedItem)
          this.selectItem = newSelectedItem;
        this._showToastService.showToast(this._i18nService.instant('GLOBAL.AN_ERROR_HAS_OCCURRED'), 'error');
      }
    });
  }

  getMessage(): string {
    let message = '';
    if (this.subtypeInvalid) {
      message = this._i18nService.instant('AUTHENTICATION.OPTION_INVALID_TYPE_SUBTYPE')
        .replace('{0}', this.typeInvalid)
        .replace('{1}', this.subtypeInvalid);
    } else {
      message = this._i18nService.instant('AUTHENTICATION.OPTION_INVALID_TYPE_SUBTYPE')
        .replace('{0}', this.typeInvalid)
        .replace('-{1}', '');
    }
    return message;
  }
  // ==================== END SERVICES ====================

  // ==================== VALIDATION ====================
  private checkAuthenticationIsValid(): void {
    this._authenticationService.getAuthenticationIsValid().subscribe({
      next: (isValid: boolean) => {
        this.isValidAuthentication = isValid;

        if (!isValid) {
          this.typeInvalid = this._authenticationDataContent.active || '';
          this.subtypeInvalid = this._authenticationDataContent.activeSubtype || '';
          this.showErrorHeader = true;
        } else {
          this.typeInvalid = '';
          this.subtypeInvalid = '';
          this.showErrorHeader = false;
        }
      },
      error: () => {
        this.showErrorHeader = false;
        this.typeInvalid = '';
        this.subtypeInvalid = '';
      }
    });
  }

  // ==================== END VALIDATION ====================

  // ==================== CHANGE SELECTION ====================
  onSelectedTypeChange(typeSelected) {
    // Inicializar selectedMethod si no existe
    if (!this.selectedMethod) {
      this.selectedMethod = {
        type: typeSelected,
        options: {}
      } as IAuthenticationMethod;
    } else {
      this.selectedMethod.type = typeSelected;
    }
    
    const existingMethod = this.foundMethodAuthentication(typeSelected, null);
    if (existingMethod) {
      this.selectedMethod = JSON.parse(JSON.stringify(existingMethod));
    }
    
    this.setSubtype();
    this.setFlow();
  }

  onSelectedSubtypeChange(subtype) {
    if (!this.selectedMethod) {
      return;
    }
    
    if (this.selectedMethod.type !== AuthenticationTypes.MULTIPLE) {
      this.selectedMethod.subtype = subtype;
      
      const existingMethod = this.foundMethodAuthentication(this.selectedMethod.type, subtype);
      if (existingMethod) {
        this.selectedMethod = JSON.parse(JSON.stringify(existingMethod));
      }
    } else {
      this.selectedMethod.subtype = null;
    }
    
    this.setFlow();
  }

  onSelectedFlowChange(flow) {
    this.setFlow(flow);
    this.updateSelectedMethodFromData();
  }

  onSelectItem(newSelectedItem: any) {
    if (newSelectedItem){
      this.getSelectedMetadata();
      const selectedMethod = this.returnSelectedMethod();
      if (newSelectedItem.value === '' && this.templates[selectedMethod.type]) {
        this.templateToShow  = this.templates[selectedMethod.type][newSelectedItem.key];
      }
      if (!this.selectItem) {
        this.selectItem = newSelectedItem;
        return;
      }
      if (this.hasPendingChanges) {
        this.showWarningUnsavedChanges(newSelectedItem);
      } else {
        this.selectItem = newSelectedItem;
      }
    } else 
      this.selectItem = null;
  }

  onUpdateItem(updatedItem: any, newSelectedItem?: any) {
    const selectedMethod = this.returnSelectedMethod();
    if (selectedMethod) {
      let properties = this._authListUtils.returnItems(selectedMethod);
      const itemIndex = properties.findIndex(item => 
        item.name && item.name === updatedItem.item.name || item.key && item.key === updatedItem.item.key
      );
      
      if (itemIndex !== -1) {
        // Update the value in the properties array
        properties[itemIndex] = { ...properties[itemIndex], value: updatedItem.item.value };

        this.updateCertificateNameFields(properties, updatedItem);

        if (selectedMethod.type !== AuthenticationTypes.OAUTH2) {
          selectedMethod[AuthenticationProperties.OPTIONS][updatedItem.item.key] = updatedItem.item.value;
        } else
        {
          // Find the specific flow that matches the activeFlow
          const activeFlow = this.activeFlow ? this.activeFlow : selectedMethod[AuthenticationProperties.OAUTH_OPTIONS]?.[AuthenticationProperties.ACTIVE_FLOW];
          const targetFlow = selectedMethod[AuthenticationProperties.OAUTH_OPTIONS].flows.find(flow => flow.name === activeFlow);
          if (targetFlow && targetFlow.properties) {
            // Find the specific property in the flow's properties array and update its value
            const flowPropertyIndex = targetFlow.properties.findIndex(prop => 
              (prop.name && prop.name === updatedItem.item.name) || 
              (prop.key && prop.key === updatedItem.item.key)
            );
            if (flowPropertyIndex !== -1) {
              targetFlow.properties[flowPropertyIndex] = { 
                ...targetFlow.properties[flowPropertyIndex], 
                value: updatedItem.item.value 
              };
            }
          }
        }
        this.updateMultipleAuthenticator(updatedItem);
        this.saveAuthentication(newSelectedItem);
      }
    }
  }

  onAddMultipleAuthenticator() {
    this.addNewMultipleAuthenticator = true;
  }

  onSelectMultipleAuthenticator(auth: any) {
    this.selectedMultipleAuthenticator = auth;
    this.indexCurrentAuthMultiple = this._authenticationDataContentOriginal.multipleMethod.authenticators.findIndex(a => a.displayName === auth.displayName);
  }

  onUnselectMultipleAuthenticator() {
    this.selectedMultipleAuthenticator = null;
    this.selectItem = null;
  }

  onCancelNewAuthenticator() {
    this.addNewMultipleAuthenticator = false;
    this.selectedMultipleAuthenticator = null;
  }

  onCreateMultipleAuthenticator(authenticator: any) {
    // Validate the authenticator before adding
    const validationResult = this.validateMultipleAuthenticator(authenticator);
    if (!validationResult.isValid) {
      this._showToastService.showToast(validationResult.errorMessage, 'error');
      return;
    }

    // Save the authenticator with all its data
    let foundMethod = this.foundMethodAuthentication(authenticator.type, authenticator.subtype);
    if (foundMethod) {
      const selectedMethod = this._authenticationDataContentOriginal.multipleMethod.authenticators[this.indexCurrentAuthMultiple];
      const isTheSameMethod = !this.addNewMultipleAuthenticator && 
                            selectedMethod.type === authenticator.type && 
                            selectedMethod.subtype === authenticator.subtype &&
                            (foundMethod.type === AuthenticationTypes.OAUTH2 ? 
                            authenticator.oauthOptions.activeFlow === selectedMethod.oauthOptions.activeFlow : true);

      if (foundMethod.type === AuthenticationTypes.OAUTH2) {
        authenticator.oauthOptions[AuthenticationProperties.ACTIVE_FLOW] = authenticator.oauthOptions.activeFlow;
        authenticator.oauthOptions.flows = isTheSameMethod ?  authenticator[AuthenticationProperties.OAUTH_OPTIONS].flows : foundMethod[AuthenticationProperties.OAUTH_OPTIONS].flows ;
      } else {
        authenticator.options = isTheSameMethod ?  authenticator[AuthenticationProperties.OPTIONS] : foundMethod[AuthenticationProperties.OPTIONS] ;
      }
    }

    if (this.addNewMultipleAuthenticator)
      this._authenticationDataContent.multipleMethod.authenticators.push(authenticator);
    else 
    {
      // Find the index of the authenticator to update
      const index = this._authenticationDataContent.multipleMethod.authenticators.findIndex(
        auth => auth.displayName === this.selectedMultipleAuthenticator?.displayName
      );
      
      if (index > -1) {
        // Update the existing authenticator with new data
        this._authenticationDataContent.multipleMethod.authenticators[index] = authenticator;
        
        // Update the selected authenticator reference
        this.selectedMultipleAuthenticator = authenticator;
      }
    }
    
    this.saveAuthentication();
  }

  onDeleteMultipleAuthenticators(authenticatorsToDelete: any[]) {
    authenticatorsToDelete.forEach(authenticatorToDelete => {
      const index = this._authenticationDataContent.multipleMethod.authenticators.findIndex(
        auth => auth.displayName === authenticatorToDelete
      );
      if (index > -1) {
        this._authenticationDataContent.multipleMethod.authenticators.splice(index, 1);
      }
    });

    this.saveAuthentication();
  }
  // ==================== END CHANGE SELECTION ====================

  // ==================== SET SECTION ====================
  setSubtype() {
    if (this.subtypes && this.subtypes.length > 0) {
      // If the selected type is the same as the active one and already had an activeSubtype, keep it
      if (this._authenticationDataContent.active === this.selectedMethod.type && this._authenticationDataContent.activeSubtype) {
        this.selectedMethod.subtype = this._authenticationDataContent.activeSubtype;
      } else {
        // If it's a different type or didn't have activeSubtype, assign the first one
        this.selectedMethod.subtype = this.subtypes[0];
      }
    }
  }

  setFlow(flow = null) {
    this.selectItem = null;
    if (this.selectedMethod.type !== AuthenticationTypes.MULTIPLE) {
      this.selectedMultipleAuthenticator = null;
    }
    this.updateSelectedMethodFromData();
    this.getSelectedMetadata();
    if (this.flows && this.flows.length > 0) {
      if (flow && flow !== this.activeFlow) {
        this.activeFlow = flow;
      }
    }
  }

  setValuesToSave() {
    this._authenticationDataContent.active = this.selectedMethod.type;
    this._authenticationDataContent.activeSubtype = this.selectedMethod.type !== AuthenticationTypes.MULTIPLE ? (this.selectedMethod.subtype ?? '') : '';
  }

  setPendingChanges(hasChanges: boolean) {
    this.hasPendingChanges = hasChanges;
  }

  setCurrentFileName(fileName: string) {
    this.currentFileName = fileName;
  }
  // ==================== END SET SECTION ====================

  // ==================== UTILS ====================
  loadActiveAuthenticationType() {
    if (this._authenticationDataContent.active) {
      this.initialAuthenticationType = this._authenticationDataContent.active;

      let foundMethod = this.foundMethodAuthentication(this._authenticationDataContent.active, this._authenticationDataContent.activeSubtype);
      // Create a deep copy of the object to avoid references
      if (foundMethod) {
        this.selectedMethod = JSON.parse(JSON.stringify(foundMethod));
        // If it's OAuth2, also load the current activeFlow
        if (this.selectedMethod.type === AuthenticationTypes.OAUTH2 && (this.selectedMethod as any)[AuthenticationProperties.OAUTH_OPTIONS]?.[AuthenticationProperties.ACTIVE_FLOW]) {
          this.activeFlow = (this.selectedMethod as any)[AuthenticationProperties.OAUTH_OPTIONS][AuthenticationProperties.ACTIVE_FLOW];
        }
      }
    }
  }

  loadAuthenticatorList() {
    if (this._authenticationDataContent && this._authenticationDataContent.multipleMethod) {
      this.authenticatorList = this._authenticationDataContent.multipleMethod.authenticators || [];
    } else {
      this.authenticatorList = [];
    }
  }

  returnSelectedMethod() {
    return this.selectedMethod.type === AuthenticationTypes.MULTIPLE && this.selectedMultipleAuthenticator ? this.selectedMultipleAuthenticator : this.selectedMethod;
  }

  saveAuthentication(newSelectedItem?: any) {
    this.addNewMultipleAuthenticator = false;
    this.setValuesToSave();
    this.updateActiveFlowInDataContent();
    const methodIndex = this.findIndexMethodAuthentication();
    const selectedMethod = this.selectedMethod.type === AuthenticationTypes.MULTIPLE && this.selectedMultipleAuthenticator ? this._authenticationDataContent.multipleMethod : this.selectedMethod;
    if (methodIndex !== -1) {
      this._authenticationDataContent.methods[methodIndex] = { ...selectedMethod };
    } else {
      this._authenticationDataContent.methods.push({ ...selectedMethod });
    }
    const dataToSave = {
      ...this._authenticationData[0],
      content: {
        ...this._authenticationDataContent,
        methods: this._authenticationDataContent.methods,
        multipleMethod: this._authenticationDataContent.multipleMethod
      }
    };
    this.saveAuthenticationService(dataToSave, newSelectedItem);
  }

  updateMultipleAuthenticator(updatedItem: any) {
    if (this.selectedMethod.type === AuthenticationTypes.MULTIPLE && this.selectedMultipleAuthenticator) {
      // Update the authenticator in the multipleMethod list
      const authIndex = this._authenticationDataContent.multipleMethod.authenticators.
        findIndex(auth => auth.displayName === this.selectedMultipleAuthenticator.displayName);
      if (authIndex !== -1) {
        const selectedMethod = this.returnSelectedMethod();
        if (selectedMethod.type !== AuthenticationTypes.OAUTH2)
          this._authenticationDataContent.multipleMethod.authenticators[authIndex].options[updatedItem.item.key || updatedItem.item.name] = updatedItem.item.value;
        else
        {
          const flow = this._authenticationDataContent.multipleMethod.authenticators[authIndex].oauthOptions.flows.find(flow => flow.name === selectedMethod[AuthenticationProperties.OAUTH_OPTIONS]?.[AuthenticationProperties.ACTIVE_FLOW]);
          if (flow && flow.properties) {
            // Find the specific property in the properties array
            const propertyIndex = flow.properties.findIndex(prop => 
              (prop.name && prop.name === updatedItem.item.name) || 
              (prop.key && prop.key === updatedItem.item.key)
            );
            if (propertyIndex !== -1) {
              // Update the property value
              flow.properties[propertyIndex] = {
                ...flow.properties[propertyIndex],
                value: updatedItem.item.value
              };
            }
          }
        }
      }
    }
  }
  
  showWarningUnsavedChanges(newSelectedItem: any) {
    const modal = this._modalService.open(ModalConfirmationComponent, {
      closeOnBackdropClick: false,
      closeOnRouterNavigation: true,
        data: { 
          titleModal: 'GLOBAL.PENDING_CHANGES_TITLE', 
          messageModal: 'GLOBAL.PENDING_CHANGES', 
          messageButtonConfirmation: 'GLOBAL.SAVE',
          typeButton: 'success',
          colorHeader: 'neutral',
          messageButtonCancel: 'GLOBAL.DISCARD'
        }
    });
    modal.afterClosed().subscribe((result) => {
      if (result === 'confirm') {
        this.encryptPassword(newSelectedItem);
      } else  {
        this.selectItem = newSelectedItem;
      }
    });
  }

  formatContent(response: any): any {
    const content = response[0].content;
    content.methods = JSON.parse(content.methods);
    content.multipleMethod = content.multipleMethod
      ? JSON.parse(content.multipleMethod)
      : this.getEmptyMultipleAuthentication();
    content.methods.push({ options: {}, type: AuthenticationTypes.MULTIPLE });

    return content;
  }

  navigateWindowMaintenance() {
    this._router.navigate(['mc', 'Maintenance']);
  }

  async encryptPassword(newSelectedItem) {
    if(this.selectedMetadata[(this.selectItem.key || this.selectItem.name)].type === 'password') {
      this.selectItem.value = await this._cryptoDataService.encryptStringPromise(this.selectItem.value);
    }
    this.onUpdateItem({ item: this.selectItem, fileName: this.currentFileName }, newSelectedItem);
  }

  private getSelectedMetadata() {
    if (!this.selectedMethod) {
      this.selectedMetadata = null;
      return;
    }
    // Determine type and subtype based on authentication method
    const isMultipleAuth = this.selectedMethod.type === AuthenticationTypes.MULTIPLE && this.selectedMultipleAuthenticator;
    const type = isMultipleAuth ? this.selectedMultipleAuthenticator.type : this.selectedMethod.type;
    const subtype = isMultipleAuth ? 
      (this.selectedMultipleAuthenticator.subtype || null) : 
      (this.selectedMethod.subtype || null);

    let metadataForType = this.getMetadataForAuthType(type);
    if (!metadataForType) {
      this.selectedMetadata = null;
      return;
    }

    // Handle different authentication scenarios
    if (type === AuthenticationTypes.OAUTH2) {
      this.selectedMetadata = this.getOAuth2Metadata(metadataForType);
    } else if (subtype) {
      this.selectedMetadata = this.getSubtypeMetadata(metadataForType);
    } else {
      this.selectedMetadata = this.getBaseMetadata(metadataForType);
    }
  }

  private getMetadataForAuthType(authType: string): any {
    const metadataEntry = Object.entries(this.metadata).find(([key]) => key === authType);
    return metadataEntry ? metadataEntry[1] : null;
  }

  private getBaseMetadata(metadataValue: any): any {
    return metadataValue?.[AuthenticationProperties.OPTIONS] || null;
  }

  private getSubtypeMetadata(metadataValue: any): any {
    const options = metadataValue?.[AuthenticationProperties.OPTIONS];
    const selectedMethod = this.returnSelectedMethod();

    if (!options || !selectedMethod.subtype) {
      return null;
    }

    const subtypeEntry = Object.entries(options).find(([key]) => key === selectedMethod.subtype);
    return subtypeEntry ? subtypeEntry[1] : null;
  }

  private getOAuth2Metadata(metadataValue: any): any {
    const selectedMethod = this.returnSelectedMethod();
    const oauthOptions = selectedMethod[AuthenticationProperties.OAUTH_OPTIONS];
    const activeFlow = oauthOptions?.[AuthenticationProperties.ACTIVE_FLOW];

    // Get subtype options first
    const subtypeMetadata = this.getSubtypeMetadata(metadataValue);

    // Find the specific flow within the subtype
    const flowEntry = Object.entries(subtypeMetadata).find(([key]) => key === activeFlow);
    return flowEntry ? flowEntry[1] : subtypeMetadata;
  }

  private getEmptyMultipleAuthentication() {
    return {
      authenticators: []
    };
  }

  private validateMultipleAuthenticator(authenticator: any): { isValid: boolean, errorMessage: string } {
    // Check if an authenticator with the same displayName already exists
    let listToSearch = this.addNewMultipleAuthenticator ? this._authenticationDataContent.multipleMethod.authenticators :
      this._authenticationDataContent.multipleMethod.authenticators.filter(auth => auth.displayName !== this.selectedMultipleAuthenticator?.displayName);
    
    const existingAuthenticator = listToSearch.find(
      auth => auth.displayName === authenticator.displayName
    );

    if (existingAuthenticator) {
      return {
        isValid: false,
        errorMessage: this._i18nService.instant('AUTHENTICATION.ERROR_AUTHENTICATORSREPEATEDDISPLAYNAME')
      };
    }
    // Check if any domain name from the new authenticator already exists in other authenticators
    const allExistingDomainNames = listToSearch
      .flatMap(auth => auth.domains || [])
      .map(domain => domain.name);

    const duplicateDomainNames = authenticator.domains.filter(newDomain => 
      allExistingDomainNames.includes(newDomain.name)
    );

    if (duplicateDomainNames.length > 0) {
      const duplicateNames = duplicateDomainNames.map(domain => domain.name).join(', ');
      return {
        isValid: false,
        errorMessage: this._i18nService.instant('AUTHENTICATION.ERROR_DOMAINSREPEATEDNAME') + ': ' + duplicateNames
      };
    }

    // Check if any domain displayName from the new authenticator already exists in other authenticators
    const allExistingDomainDisplayNames = listToSearch
      .flatMap(auth => auth.domains || [])
      .map(domain => domain.displayName);

    const duplicateDomainDisplayNames = authenticator.domains.filter(newDomain => 
      allExistingDomainDisplayNames.includes(newDomain.displayName)
    );

    if (duplicateDomainDisplayNames.length > 0) {
      const duplicateDisplayNames = duplicateDomainDisplayNames.map(domain => domain.displayName).join(', ');
      return {
        isValid: false,
        errorMessage: this._i18nService.instant('AUTHENTICATION.ERROR_DOMAINSREPEATEDDISPLAYNAME') + ': ' + duplicateDisplayNames
      };
    }

    return { isValid: true, errorMessage: '' };
  }

  private foundMethodAuthentication(active, activeSubtype) {
    let foundMethod: IAuthenticationMethod;
      
    // If activeSubtype is empty, search only by type
    if (!activeSubtype || activeSubtype === '') {
      foundMethod = this._authenticationDataContent.methods.find(method => 
        method.type === active);
    } else {
      // If activeSubtype has value, search by type and subtype
      foundMethod = this._authenticationDataContent.methods.find(method => 
        method.type === active && 
        method.subtype === activeSubtype);
    }

    return foundMethod;
  }

  private findIndexMethodAuthentication() {
    let methodIndex: number;
    
    // If selectedMethod has no subtype or is empty, search only by type
    if (!this.selectedMethod.subtype || this.selectedMethod.subtype === '') {
      methodIndex = this._authenticationDataContent.methods.findIndex(method => 
        method.type === this.selectedMethod.type);
    } else {
      // If selectedMethod has subtype, search by type and subtype
      methodIndex = this._authenticationDataContent.methods.findIndex(method => 
        method.type === this.selectedMethod.type && 
        method.subtype === this.selectedMethod.subtype);
    }
    return methodIndex;
  }

  private updateCertificateNameFields(properties: any[], updatedItem: any): void {
    // Define certificate mapping for maintainability
    const certificateMapping = {
      'encryptionCertificate': 'encryptionCertificateName',
      'signatureCertificate': 'signatureCertificateName',
      'customAuthenticationComponent': 'customAuthenticationName'
    };

    const certificateKey = updatedItem.item.key;
    const nameFieldKey = certificateMapping[certificateKey];
    if (nameFieldKey && updatedItem.fileName) {
      // Update in properties array (for OAuth flows)
      const nameFieldIndex = properties.findIndex(item => 
        (item.name && item.name === nameFieldKey) || 
        (item.key && item.key === nameFieldKey)
      );
      
      if (nameFieldIndex !== -1) {
        properties[nameFieldIndex] = { 
          ...properties[nameFieldIndex], 
          value: updatedItem.fileName 
        };
      } else {
        properties.push({ 
          key: nameFieldKey, 
          value: updatedItem.fileName 
        });
      }

      const selectedMethod = this.returnSelectedMethod();

      if (selectedMethod[AuthenticationProperties.OPTIONS]) {
        selectedMethod[AuthenticationProperties.OPTIONS][nameFieldKey] = updatedItem.fileName;
      } else {
        const activeFlow = selectedMethod[AuthenticationProperties.OAUTH_OPTIONS].activeFlow;
        const flows = selectedMethod[AuthenticationProperties.OAUTH_OPTIONS].flows;
            
        if (activeFlow && flows && flows.length > 0) {
          const targetFlowIndex = flows.findIndex(flow => flow.name === activeFlow);
          selectedMethod[AuthenticationProperties.OAUTH_OPTIONS].flows[targetFlowIndex].properties[nameFieldKey] = updatedItem.fileName;
        } 
      }
    }
  }

  private updateActiveFlowInDataContent() {
    if (this.selectedMethod.type === AuthenticationTypes.OAUTH2) {
      const methodActiveFlow = (this.selectedMethod as any)[AuthenticationProperties.OAUTH_OPTIONS][AuthenticationProperties.ACTIVE_FLOW];
      
      const updatedAuthenticationContent = {
        ...this._authenticationDataContent,
        activeFlow: methodActiveFlow
      };

      const updatedMethods = updatedAuthenticationContent.methods.map(method => {
        if (method.type === this.selectedMethod.type &&
          method.subtype === (this.selectedMethod as any).subtype) {
          return {
            ...method,
            oauthOptions: {
              ...method.oauthOptions,
              activeFlow: methodActiveFlow
            }
          };
        }
        return method;
      });

      this._authenticationDataContent = {
        ...updatedAuthenticationContent,
        methods: updatedMethods
      };
    }
  }

  private updateSelectedMethodFromData(): void {
    if (!this.selectedMethod || !this._authenticationDataContent) {
      return;
    }

    // Find the current method in the data structure
    const foundMethod = this.foundMethodAuthentication(this.selectedMethod.type, this.selectedMethod.subtype);
    
    if (foundMethod) {
      // Create a deep copy to avoid reference issues
      
      this.selectedMethod = JSON.parse(JSON.stringify(foundMethod));
      // If the type has subtypes, ensure subtype is set
      if (this.subtypes && this.subtypes.length > 0) {
        if (!this.selectedMethod.subtype) {
          this.selectedMethod.subtype = this._authenticationDataContent.activeSubtype || this.subtypes[0];
        }
      }
      
      // If it's OAuth2, ensure the flow is properly set
      if (this.selectedMethod.type === AuthenticationTypes.OAUTH2) {
        // Ensure oauthOptions structure exists
        if (!this.selectedMethod[AuthenticationProperties.OAUTH_OPTIONS]) {
          this.selectedMethod[AuthenticationProperties.OAUTH_OPTIONS] = {};
        }
        
        // Set the active flow
        if (this.flows && this.flows.length > 0) {
          const currentActiveFlow = this.activeFlow;
          if (currentActiveFlow) {
            this.selectedMethod[AuthenticationProperties.OAUTH_OPTIONS][AuthenticationProperties.ACTIVE_FLOW] = currentActiveFlow;
          } else {
            // If no active flow is set, use the first available flow
            this.selectedMethod[AuthenticationProperties.OAUTH_OPTIONS][AuthenticationProperties.ACTIVE_FLOW] = this.flows[0];
            this.activeFlow = this.flows[0];
          }
        }
      }
    }
  }

  // ==================== END UTILS ====================
}
